import { z } from 'zod';
import { ORIENTATIONS } from './manifest';

/**
 * A game's store listing (M20): how it shows on MOGZO, on its game page and its card in lists
 * and search. The developer fills it in on the dashboard's Store listing tab; it can be saved
 * unfinished, and submitting needs everything `listingChecklist` asks for.
 */

export const LISTING_LIMITS = {
  title: 40,
  /** The name the game is by on MOGZO, such as a studio's. */
  developer: 40,
  shortDescription: 80,
  about: 4_000,
  tags: 5,
  screenshotsMin: 3,
  screenshotsMax: 8,
  /** Characters of text in How to play, all blocks together. */
  howToPlay: 10_000,
  howToPlayPictures: 6,
  howToPlayBlocks: 200,
  caption: 160,
  trailer: 300,
} as const;

export const GENRES = [
  'action',
  'adventure',
  'arcade',
  'board-and-card',
  'casual',
  'puzzle',
  'racing',
  'shooter',
  'simulation',
  'sports',
  'strategy',
  'word',
] as const;
export type Genre = (typeof GENRES)[number];
export const GENRE_NAMES: Record<Genre, string> = {
  action: 'Action',
  adventure: 'Adventure',
  arcade: 'Arcade',
  'board-and-card': 'Board and card',
  casual: 'Casual',
  puzzle: 'Puzzle',
  racing: 'Racing',
  shooter: 'Shooter',
  simulation: 'Simulation',
  sports: 'Sports',
  strategy: 'Strategy',
  word: 'Word',
};

/** MOGZO's tags; a game picks up to five, so players can find it. */
export const LISTING_TAGS = {
  'one-button': 'One button',
  'high-score': 'High score',
  endless: 'Endless',
  levels: 'Levels',
  'short-rounds': 'Short rounds',
  relaxing: 'Relaxing',
  'fast-paced': 'Fast-paced',
  'hard-to-master': 'Hard to master',
  'pixel-art': 'Pixel art',
  '3d': '3D',
  'hand-drawn': 'Hand-drawn',
  retro: 'Retro',
  cute: 'Cute',
  space: 'Space',
  fantasy: 'Fantasy',
  'sci-fi': 'Sci-fi',
  animals: 'Animals',
  cars: 'Cars',
  physics: 'Physics',
  platformer: 'Platformer',
  'match-3': 'Match 3',
  idle: 'Idle',
  'tower-defense': 'Tower defense',
  quiz: 'Quiz',
  drawing: 'Drawing',
  music: 'Music',
  story: 'Story',
  daily: 'Daily challenge',
  'two-players': 'Two players',
  multiplayer: 'Multiplayer',
} as const;
export type ListingTag = keyof typeof LISTING_TAGS;
const TAG_IDS = Object.keys(LISTING_TAGS) as [ListingTag, ...ListingTag[]];

export const LISTING_DEVICES = ['computer', 'phone'] as const;
export type ListingDevice = (typeof LISTING_DEVICES)[number];

export const LISTING_IMAGE_KINDS = ['icon', 'cover', 'screenshot', 'howto'] as const;
export type ListingImageKind = (typeof LISTING_IMAGE_KINDS)[number];
export const LISTING_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;

/** What each kind of listing picture must be, and the size players get. */
export const LISTING_IMAGE_RULES = {
  icon: { maxBytes: 1_048_576, minSide: 512, maxSide: 2_048, out: 512 },
  cover: {
    maxBytes: 4_194_304,
    minWidth: 1_920,
    minHeight: 1_080,
    out: { width: 1_920, height: 1_080 },
  },
  screenshot: { maxBytes: 4_194_304, minLongSide: 1_280, outLongSide: 1_920 },
  howto: { maxBytes: 2_097_152, minLongSide: 320, outLongSide: 1_600 },
} as const;
/** The biggest picture of any kind, for the upload's body limit. */
export const LISTING_IMAGE_BYTES_MAX = 4_194_304;

/** One picture's rule, as the editor says it. */
export const LISTING_IMAGE_HINTS: Record<ListingImageKind, string> = {
  icon: 'Square, 512 to 2,048 px, up to 1 MB',
  cover: '16 : 9, at least 1920 × 1080 px, up to 4 MB',
  screenshot: 'At least 1280 px on the long side, up to 4 MB',
  howto: 'Up to 2 MB',
};

export const RATING_QUESTIONS = [
  {
    id: 'violence',
    question: 'Does it show violence?',
    answers: [
      ['none', 'None'],
      ['cartoon', 'Cartoon or fantasy'],
      ['realistic', 'Realistic'],
    ],
  },
  {
    id: 'scary',
    question: 'Anything scary or gory?',
    answers: [
      ['no', 'No'],
      ['some', 'A little'],
      ['lots', 'A lot'],
    ],
  },
  {
    id: 'language',
    question: 'Swearing or crude language?',
    answers: [
      ['no', 'No'],
      ['mild', 'Mild'],
      ['strong', 'Strong'],
    ],
  },
  {
    id: 'chance',
    question: 'Betting, or prizes left to chance, such as loot boxes?',
    answers: [
      ['no', 'No'],
      ['yes', 'Yes'],
    ],
  },
  {
    id: 'sharing',
    question: 'Can players write to each other or share things inside the game?',
    help: 'MOGZO’s own posts and comments don’t count; they have their own rules.',
    answers: [
      ['no', 'No'],
      ['yes', 'Yes'],
    ],
  },
] as const;
type Question = (typeof RATING_QUESTIONS)[number];
export type RatingAnswers = {
  [Q in Question as Q['id']]?: Q['answers'][number][0];
};

export const RATINGS = ['everyone', '16', '18'] as const;
export type Rating = (typeof RATINGS)[number];
export const RATING_NAMES: Record<Rating, string> = {
  everyone: 'Everyone',
  '16': '16+',
  '18': '18+',
};

/** The rating a game's answers give; unanswered questions count as the mildest answer. */
export function ratingOf(answers: RatingAnswers): Rating {
  if (answers.scary === 'lots' || answers.chance === 'yes') return '18';
  if (
    answers.violence === 'realistic' ||
    answers.scary === 'some' ||
    answers.language === 'strong'
  ) {
    return '16';
  }
  return 'everyone';
}

// --- How to play ---------------------------------------------------------------------------

/** A run of text, possibly bold, italic or drawn as a key. */
const inlineSchema = z.strictObject({
  text: z.string().min(1).max(LISTING_LIMITS.howToPlay),
  bold: z.literal(true).optional(),
  italic: z.literal(true).optional(),
  key: z.literal(true).optional(),
});
export type ListingInline = z.infer<typeof inlineSchema>;
const inlines = z.array(inlineSchema).max(500);

const blockSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('heading'), level: z.literal([2, 3]), text: inlines }),
  z.strictObject({ type: z.literal('paragraph'), text: inlines }),
  z.strictObject({
    type: z.literal('list'),
    ordered: z.boolean(),
    items: z.array(inlines).min(1).max(100),
  }),
  z.strictObject({ type: z.literal('tip'), text: inlines }),
  z.strictObject({
    type: z.literal('picture'),
    imageId: z.uuid(),
    caption: z.string().max(LISTING_LIMITS.caption),
  }),
]);
export type ListingBlock = z.infer<typeof blockSchema>;

/** Every run of text in How to play, captions included. */
export function howToPlayText(blocks: readonly ListingBlock[]): string {
  const runs = (text: ListingInline[]) => text.map((run) => run.text).join('');
  return blocks
    .map((block) =>
      block.type === 'picture'
        ? block.caption
        : block.type === 'list'
          ? block.items.map(runs).join('\n')
          : runs(block.text),
    )
    .join('\n');
}

export const howToPlaySchema = z
  .array(blockSchema)
  .max(LISTING_LIMITS.howToPlayBlocks)
  .superRefine((blocks, ctx) => {
    if (howToPlayText(blocks).length > LISTING_LIMITS.howToPlay) {
      ctx.addIssue({
        code: 'custom',
        message: `How to play can be up to ${LISTING_LIMITS.howToPlay.toLocaleString('en')} characters.`,
      });
    }
    if (blocks.filter((b) => b.type === 'picture').length > LISTING_LIMITS.howToPlayPictures) {
      ctx.addIssue({
        code: 'custom',
        message: `How to play can have up to ${LISTING_LIMITS.howToPlayPictures} pictures.`,
      });
    }
  });

// --- The listing ---------------------------------------------------------------------------

const TRAILER =
  /^https:\/\/(www\.)?(youtube\.com\/watch\?v=[\w-]{6,}|youtu\.be\/[\w-]{6,}|vimeo\.com\/\d{5,})([?&#][\w=&.-]*)?$/;
const LINK = /\b(https?:\/\/|www\.)/i;

const unique = <T>(list: T[]) => new Set(list).size === list.length;

export const listingSchema = z.strictObject({
  title: z.string().trim().max(LISTING_LIMITS.title),
  // Listings saved before it existed have none.
  developer: z
    .string()
    .trim()
    .max(LISTING_LIMITS.developer)
    .refine((text) => !LINK.test(text), 'Links aren’t allowed in the developer name.')
    .default(''),
  shortDescription: z
    .string()
    .trim()
    .max(LISTING_LIMITS.shortDescription)
    .refine((text) => !LINK.test(text), 'Links aren’t allowed in the short description.'),
  about: z
    .string()
    .max(LISTING_LIMITS.about)
    .refine((text) => !LINK.test(text), 'Links aren’t allowed in About the game.'),
  genre: z.enum(GENRES).nullable(),
  tags: z.array(z.enum(TAG_IDS)).max(LISTING_LIMITS.tags).refine(unique, 'Each tag once.'),
  iconId: z.uuid().nullable(),
  coverId: z.uuid().nullable(),
  screenshotIds: z
    .array(z.uuid())
    .max(LISTING_LIMITS.screenshotsMax)
    .refine(unique, 'Each screenshot once.'),
  trailerUrl: z
    .string()
    .trim()
    .max(LISTING_LIMITS.trailer)
    .refine((url) => url === '' || TRAILER.test(url), 'Use a YouTube or Vimeo link.'),
  howToPlay: howToPlaySchema,
  orientation: z.enum(ORIENTATIONS),
  devices: z.array(z.enum(LISTING_DEVICES)).max(2).refine(unique, 'Each device once.'),
  rating: z.strictObject({
    violence: z.enum(['none', 'cartoon', 'realistic']).optional(),
    scary: z.enum(['no', 'some', 'lots']).optional(),
    language: z.enum(['no', 'mild', 'strong']).optional(),
    chance: z.enum(['no', 'yes']).optional(),
    sharing: z.enum(['no', 'yes']).optional(),
  }) satisfies z.ZodType<RatingAnswers>,
});
export type Listing = z.infer<typeof listingSchema>;

/** A listing nobody has saved yet: the project's name, and the newest build's screen and devices. */
export function emptyListing(
  name: string,
  start: { orientation: Listing['orientation']; devices: ListingDevice[] } = {
    orientation: 'any',
    devices: ['computer', 'phone'],
  },
): Listing {
  return {
    title: name.slice(0, LISTING_LIMITS.title),
    developer: '',
    shortDescription: '',
    about: '',
    genre: null,
    tags: [],
    iconId: null,
    coverId: null,
    screenshotIds: [],
    trailerUrl: '',
    howToPlay: [],
    orientation: start.orientation,
    devices: start.devices,
    rating: {},
  };
}

export interface ChecklistItem {
  id:
    | 'words'
    | 'developer'
    | 'genre'
    | 'tags'
    | 'icon'
    | 'cover'
    | 'screenshots'
    | 'screen'
    | 'howToPlay'
    | 'rating';
  label: string;
  done: boolean;
  /** How far along, when it's a count: "2 so far", "1 left". */
  note?: string;
}

/** What submitting needs, in the order the editor shows it. */
export function listingChecklist(listing: Listing): ChecklistItem[] {
  const shots = listing.screenshotIds.length;
  const unanswered = RATING_QUESTIONS.filter((q) => !listing.rating[q.id]).length;
  return [
    {
      id: 'words',
      label: 'Title and descriptions',
      done: Boolean(listing.title && listing.shortDescription && listing.about.trim()),
    },
    { id: 'developer', label: 'Developer name', done: listing.developer !== '' },
    { id: 'genre', label: 'Genre', done: listing.genre !== null },
    { id: 'tags', label: 'At least one tag', done: listing.tags.length > 0 },
    { id: 'icon', label: 'Icon', done: listing.iconId !== null },
    { id: 'cover', label: 'Cover', done: listing.coverId !== null },
    {
      id: 'screenshots',
      label: `${LISTING_LIMITS.screenshotsMin} screenshots`,
      done: shots >= LISTING_LIMITS.screenshotsMin,
      note: shots && shots < LISTING_LIMITS.screenshotsMin ? `${shots} so far` : undefined,
    },
    { id: 'screen', label: 'Screen and devices', done: listing.devices.length > 0 },
    { id: 'howToPlay', label: 'How to play', done: howToPlayText(listing.howToPlay).trim() !== '' },
    {
      id: 'rating',
      label: 'Content rating',
      done: unanswered === 0,
      note: unanswered && unanswered < RATING_QUESTIONS.length ? `${unanswered} left` : undefined,
    },
  ];
}

/** A listing picture as the editor and preview get it. */
export interface ListingImage {
  id: string;
  kind: ListingImageKind;
  status: 'processing' | 'ready' | 'failed';
  error: string | null;
  url: string | null;
  width: number | null;
  height: number | null;
}

/** The dashboard's view of a listing: the listing, its pictures and where it started. */
export interface ListingView {
  listing: Listing;
  images: ListingImage[];
  /** False until the developer saves it; screen and devices then still come from mogzo.json. */
  saved: boolean;
  updatedAt: string | null;
  /** The newest build's mogzo.json values, shown as where screen and devices started. */
  manifest: { orientation: Listing['orientation']; devices: ListingDevice[] } | null;
  /**
   * Once the game is live (M23): the listing players see, with the version it went live with,
   * and the fields the saved draft changes.
   */
  live: { version: string; listing: Listing; images: ListingImage[]; changed: string[] } | null;
  /** A listing change sent on its own, in review or approved and not live yet. */
  pending: { id: string; status: 'in_review' | 'approved' } | null;
  /**
   * The last push from the CLI (M30), while fields it changed haven't been changed in the
   * dashboard since: the computer, when, and those fields by name.
   */
  cli: { device: string | null; at: string; fields: string[] } | null;
}
